import { NavLink, useLocation, useNavigate } from 'react-router-dom'
// Brand logo
import { 
  LayoutDashboard, 
  Upload, 
  Receipt, 
  BarChart3, 
  LogOut,
  Menu,
  MessageCircle,
  Building2,
  ChevronLeft,
  ChevronRight,
  Brain,
  Sun,
  Moon,
  Link2,
  FileText,
  Crown,
  Sparkles,
  Briefcase,
  Globe,
  X,
  ChevronDown,
  Loader2,
  Shield,
  ShieldCheck,
  Settings,
  Target,
  Database,
  User as UserIcon,
  Clock,
  Calendar,
  AlertTriangle,
  Bell,
  Wallet,
  Download,
  Search,
  Trash2,
  Plus,
  KeyRound,
} from 'lucide-react'
import { useState, useEffect, useMemo, type ComponentType } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import WorkspaceSelector from './WorkspaceSelector'
import UpgradeModal from './UpgradeModal'
import DashboardSelector from './DashboardSelector'
import { useTheme } from '../hooks/useTheme'
import { useUser } from '../hooks/useUser'
import { useLanguage } from '../hooks/useLanguage'
import api from '../services/api'
import { useFinancialMaturity, MATURITY_STATES } from '../hooks/useFinancialMaturity'
import { useActiveModules } from '../hooks/useActiveModules'

interface FiscalDeadline {
  id: string
  date: string
  title: string
  description: string
  category: 'iva' | 'irpef' | 'inps' | 'inail' | 'dichiarazioni' | 'altro'
  icon: string
  priority: 'high' | 'medium' | 'low'
}

interface FiscalDeadlinesData {
  deadlines: FiscalDeadline[]
  grouped: {
    urgent: number
    upcoming: number
    later: number
  }
  total: number
}

interface LayoutProps {
  children: React.ReactNode
  onLogout: () => void
}

interface ChatConversation {
  id: string
  title: string
  updated_at: string
}

interface UploadQueueEntry {
  id: string
  filename: string
  status: 'pending' | 'queued' | 'processing' | 'completed' | 'error'
  upload_date: string
}

const categoryIcons: Record<string, ComponentType<{ className?: string }>> = {
  tesoreria: Wallet,
  'ciclo-passivo': Receipt,
  ade: Shield,
  ai: Sparkles,
}

const REI_ENABLED = import.meta.env.VITE_REI_ENABLED === 'true'

const navCategories = [
  {
    id: 'tesoreria',
    titleIt: 'TESORERIA',
    titleEn: 'TREASURY',
    items: [
      { to: '/treasury', icon: Wallet, labelKey: 'treasury' as const },
    ]
  },

  {
    id: 'ade',
    titleIt: 'AdE',
    titleEn: 'AdE',
    items: [
      { to: '/sid', icon: Shield, labelKey: 'sid' as const, requiredPlan: 'business' as const, moduleKey: 'sid_italy' },
      { to: '/sid/storico', icon: Clock, labelKey: 'sidStorico' as const, requiredPlan: 'business' as const, moduleKey: 'sid_italy' },
      { to: '/sid/certificato', icon: KeyRound, labelKey: 'sidCertificato' as const, requiredPlan: 'business' as const, moduleKey: 'sid_italy' },
      { to: '/sid/admin', icon: Settings, labelKey: 'sidAdmin' as const, requiredPlan: 'business' as const, moduleKey: 'sid_italy' },
      ...(REI_ENABLED ? [{ to: '/rei', icon: Globe, labelKey: 'rei' as const }] : []),
      { to: '/f24', icon: FileText, labelKey: 'f24' as const },
    ]
  },
  {
    id: 'ai',
    titleIt: 'ASSISTENTE',
    titleEn: 'ASSISTANT',
    items: [
      { to: '/chat', icon: MessageCircle, labelKey: 'assistant' as const, aiEnabled: true },
      { to: '/ai-governance', icon: ShieldCheck, labelKey: 'aiGovernance' as const, aiEnabled: true },
    ]
  },
]

// ── Top section tabs (Agicap-style horizontal navigation) ──
const topSections: Array<{
  id: string
  path: string | null
  labelIt: string
  labelEn: string
  children?: string[]
  badge?: boolean
  diamond?: boolean
}> = [
  { id: 'treasury', path: null, labelIt: 'Tesoreria', labelEn: 'Treasury',
    children: ['/treasury', '/forecasts', '/business-plan'] },
  { id: 'transactions', path: '/transactions', labelIt: 'Banca', labelEn: 'Bank' },
  { id: 'reconciliation', path: '/reconciliation', labelIt: 'In attesa', labelEn: 'Pending', badge: true },
  { id: 'categorize', path: '/categorize', labelIt: 'Prima nota', labelEn: 'Journal', diamond: true },
  { id: 'accounting', path: null, labelIt: 'Contabilità', labelEn: 'Accounting',
    children: ['/invoices', '/tabellone'] },
  { id: 'analysis', path: null, labelIt: 'Area Finanziaria', labelEn: 'Financial Area',
    children: ['/reports', '/business-intelligence', '/patrimoniale'] },

  { id: 'dashboard', path: '/', labelIt: 'Dashboards', labelEn: 'Dashboards' },
]

// ── Lookup for top-nav dropdown items (pages not in sidebar navCategories) ──
const topNavItemLookup: Record<string, { icon: ComponentType<{ className?: string }>; labelIt: string; labelEn: string; descIt?: string; descEn?: string }> = {
  '/invoices': { icon: FileText, labelIt: 'Fatture', labelEn: 'Invoices', descIt: 'Gestione fatture attive e passive', descEn: 'Manage invoices' },
  '/tabellone': { icon: Database, labelIt: 'Piano dei Conti', labelEn: 'Chart of Accounts', descIt: 'Piano dei conti e mappatura', descEn: 'Chart of accounts and mapping' },
  '/treasury': { icon: Wallet, labelIt: 'Piano Tesoreria', labelEn: 'Treasury Plan', descIt: 'Flusso di cassa e previsioni', descEn: 'Cash flow and forecasts' },
  '/forecasts': { icon: Brain, labelIt: 'Previsione 13 Settimane', labelEn: '13-Week Forecast', descIt: 'Proiezioni a breve termine', descEn: 'Short-term projections' },
  '/business-plan': { icon: Target, labelIt: 'Reale e Previsionale', labelEn: 'Actual vs Forecast', descIt: 'Budget vs consuntivo', descEn: 'Budget vs actual' },
  '/reports': { icon: BarChart3, labelIt: 'Report', labelEn: 'Reports', descIt: 'Analisi e reportistica', descEn: 'Analysis and reporting' },
  '/business-intelligence': { icon: Briefcase, labelIt: 'Registro Imprese', labelEn: 'Business Register', descIt: 'Ricerca aziende e schede dal Registro Imprese', descEn: 'Company search and registry extracts' },
  '/patrimoniale': { icon: Building2, labelIt: 'Patrimoniale', labelEn: 'Balance Sheet', descIt: 'Situazione patrimoniale', descEn: 'Balance sheet overview' },
  '/reconciliation': { icon: Receipt, labelIt: 'Verifica Fatture', labelEn: 'Invoice Verification', descIt: 'Riconciliazione e matching', descEn: 'Reconciliation and matching' },
  '/categorize': { icon: Sparkles, labelIt: 'Prima Nota', labelEn: 'Journal Entries', descIt: 'Categorizzazione movimenti', descEn: 'Transaction categorization' },
  '/transactions': { icon: Wallet, labelIt: 'Pagamenti', labelEn: 'Payments', descIt: 'Movimenti bancari', descEn: 'Bank transactions' },
}

export default function Layout({ children, onLogout }: LayoutProps) {
  const navigate = useNavigate()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [collapsed, setCollapsed] = useState(true)
  const [chatSubmenuOpen, setChatSubmenuOpen] = useState(true)
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(['tesoreria', 'ade', 'ai']))
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const [showUserPanel, setShowUserPanel] = useState(false)
  const [showDashboardSelector, setShowDashboardSelector] = useState(false)
  const queryClient = useQueryClient()
  const { toggleTheme, isDark } = useTheme()
  const { user } = useUser()
  const { hasModule } = useActiveModules()
  const { language, setLanguage, t } = useLanguage()
  const location = useLocation()
  const isChatRoute = location.pathname === '/chat'
  const { maturityData, currentStateConfig } = useFinancialMaturity()
  const [showFiscalPanel, setShowFiscalPanel] = useState(false)

  // Resolve current page info from navCategories
  const currentPage = useMemo(() => {
    for (const cat of navCategories) {
      for (const item of cat.items) {
        if (item.to === location.pathname || (item.to !== '/' && location.pathname.startsWith(item.to))) {
          return { icon: item.icon, label: t.nav[item.labelKey] }
        }
      }
    }
    // fallback for root
    if (location.pathname === '/') return { icon: LayoutDashboard, label: t.nav.dashboard }
    return null
  }, [location.pathname, t.nav])

  // Fiscal deadlines query (global — visible from all pages)
  const { data: fiscalData, isLoading: fiscalLoading } = useQuery<FiscalDeadlinesData>({
    queryKey: ['fiscal-deadlines'],
    queryFn: async () => {
      const response = await api.get('/fiscal/deadlines?limit=20&days=90')
      return response.data
    },
    retry: 1,
    staleTime: 60 * 60 * 1000,
  })

  const urgentDeadlines = fiscalData?.grouped?.urgent || 0
  const upcomingDeadlines = (fiscalData?.grouped?.upcoming || 0) + urgentDeadlines

  const categoryColors: Record<string, string> = {
    iva: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
    irpef: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300',
    inps: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
    inail: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300',
    dichiarazioni: 'bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300',
    altro: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
  }
  const currentPlan = user?.plan || 'trial'
  const isTrialUser = Boolean(user?.is_trial)
  const currentPlanLabel = currentPlan === 'business'
    ? 'Business'
    : currentPlan === 'starter'
      ? 'Starter'
      : currentPlan === 'pro'
        ? 'Pro'
        : 'Trial'
  const currentPlanDisplay = isTrialUser
    ? currentPlan === 'trial'
      ? `Trial (${user?.trial_days_left ?? 0} ${t.nav.daysLeft || 'giorni rimasti'})`
      : `${currentPlanLabel} · Trial (${user?.trial_days_left ?? 0} ${t.nav.daysLeft || 'giorni rimasti'})`
    : currentPlanLabel
  
  // Show upgrade modal when trial expires
  useEffect(() => {
    if (user?.is_trial_expired) {
      setShowUpgradeModal(true)
    }
  }, [user?.is_trial_expired])
  
  // Chat history for left-menu submenu
  const activeChatId = useMemo(() => new URLSearchParams(location.search).get('c'), [location.search])

  const { data: chatHistory = [], refetch: refetchChatHistory } = useQuery({
    queryKey: ['layout-chat-history'],
    queryFn: async () => {
      const response = await api.get('/chats')
      return (response.data.chats || []) as ChatConversation[]
    },
    enabled: isChatRoute || (!collapsed || sidebarOpen),
    staleTime: 15000,
  })

  const createChatMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/chats', { title: 'Nuova conversazione' })
      return response.data.chat as ChatConversation
    },
    onSuccess: (chat) => {
      navigate(`/chat?c=${chat.id}${location.hash}`)
      setChatSubmenuOpen(true)
      setSidebarOpen(false)
    },
  })

  const deleteChatMutation = useMutation({
    mutationFn: async (chatId: string) => {
      await api.delete(`/chats/${chatId}`)
      return chatId
    },
    onSuccess: (deletedChatId) => {
      queryClient.invalidateQueries({ queryKey: ['chats'] })
      refetchChatHistory()
      if (activeChatId === deletedChatId) {
        navigate(`/chat${location.hash}`)
      }
    },
  })

  const { data: uploadQueue = [] } = useQuery({
    queryKey: ['layout-upload-queue'],
    queryFn: async () => {
      const response = await api.get('/upload/')
      return (response.data || []) as UploadQueueEntry[]
    },
    // Poll only when there are files being processed/queued, otherwise stop
    refetchInterval: (query) => {
      const data = query.state.data as UploadQueueEntry[] | undefined
      const hasActive = data?.some(item =>
        item.status === 'processing' || item.status === 'queued' || item.status === 'pending'
      )
      return hasActive ? 3000 : false
    },
    staleTime: 10000,
  })

  // Pending reconciliation count for top nav badge
  const { data: pendingCount = 0 } = useQuery({
    queryKey: ['layout-pending-count'],
    queryFn: async () => {
      try {
        const response = await api.get('/reconciliation/pending-count')
        return response.data?.count || 0
      } catch {
        return 0
      }
    },
    staleTime: 60000,
  })

  const queueProcessing = uploadQueue.filter((item) => item.status === 'processing').length
  const queueQueued = uploadQueue.filter((item) => item.status === 'queued' || item.status === 'pending').length
  const activeQueueEntries = uploadQueue
    .filter((item) => item.status === 'processing' || item.status === 'queued' || item.status === 'pending')
    .slice(0, 3)
  
  // Preserve hash when navigating
  const getNavTo = (path: string) => `${path}${location.hash}`

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 overflow-x-hidden flex flex-col">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-40 h-full bg-white dark:bg-slate-800 border-r border-gray-200 dark:border-slate-700
        transform transition-all duration-200 ease-in-out flex flex-col
        lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0 w-64' : '-translate-x-full'}
        ${!sidebarOpen && (collapsed ? 'lg:w-[4.5rem]' : 'lg:w-64')}
        ${sidebarOpen ? '' : (collapsed ? 'lg:w-[4.5rem]' : 'lg:w-64')}
      `}>
        {/* Logo - fixed at top */}
        <div className={`flex-shrink-0 flex items-center gap-2 h-14 border-b border-gray-100 dark:border-slate-700 ${(collapsed && !sidebarOpen) ? 'px-3 justify-center' : 'px-4'}`}>
          <div className="logo-sphere" style={collapsed && !sidebarOpen ? { width: 22, height: 22 } : undefined} />
          {(!collapsed || sidebarOpen) && (
            <div className="flex flex-col leading-none">
              <span className="text-sm font-bold tracking-tight text-gray-900 dark:text-white font-display">PongoFin</span>
              <span className="text-[8px] font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider font-display mt-[1px]">Financial Intelligence</span>
            </div>
          )}
        </div>



        {/* Toggle Button (desktop only) */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className={`hidden lg:flex absolute -right-3 top-[2.875rem] w-6 h-6 ${collapsed ? 'bg-primary-600 border-primary-500 hover:bg-primary-700' : 'bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-600'} border rounded-full items-center justify-center shadow-sm transition-colors`}
        >
          {collapsed ? <ChevronRight className="w-3.5 h-3.5 text-white" /> : <ChevronLeft className="w-3.5 h-3.5 text-gray-600 dark:text-gray-300" />}
        </button>

        {/* Navigation - scrollable */}
        <nav className={`flex-1 overflow-y-auto py-2 ${(collapsed && !sidebarOpen) ? 'px-1.5' : 'px-2.5'}`}>
          {navCategories.map((category) => {
            const hasTitle = category.titleIt !== ''
            const isGroupExpanded = expandedGroups.has(category.id)

            return (
              <div key={category.id} className={hasTitle ? ((collapsed && !sidebarOpen) ? 'mt-1 first:mt-0' : 'mt-4 first:mt-0') : ''}>
                {/* Category header - expanded */}
                {hasTitle && (!collapsed || sidebarOpen) && (
                  <button
                    type="button"
                    onClick={() => {
                      setExpandedGroups(prev => {
                        const next = new Set(prev)
                        if (next.has(category.id)) next.delete(category.id)
                        else next.add(category.id)
                        return next
                      })
                    }}
                    className={`w-full flex items-center gap-1.5 px-2.5 py-1 mb-1 text-gray-700 dark:text-white hover:text-gray-900 dark:hover:text-gray-200 transition-colors ${
                      category.id === 'ade'
                        ? 'text-[10px] font-bold tracking-wider'
                        : 'text-[10px] font-bold uppercase tracking-wider'
                    }`}
                  >
                    <ChevronDown className={`w-3 h-3 text-gray-700 dark:text-white transition-transform ${isGroupExpanded ? '' : '-rotate-90'}`} />
                    <span>{language === 'it' ? category.titleIt : category.titleEn}</span>
                  </button>
                )}

                {/* Category icon - collapsed */}
                {hasTitle && collapsed && !sidebarOpen && (() => {
                  const CatIcon = categoryIcons[category.id]
                  return (
                    <button
                      type="button"
                      onClick={() => {
                        setExpandedGroups(prev => {
                          const next = new Set(prev)
                          if (next.has(category.id)) next.delete(category.id)
                          else next.add(category.id)
                          return next
                        })
                      }}
                      className="group/cat relative w-full flex items-center justify-center py-2 rounded-md hover:bg-gray-100 dark:hover:bg-slate-700/60 transition-colors"
                    >
                      {CatIcon && <CatIcon className="w-[18px] h-[18px] text-gray-500 dark:text-slate-400" />}
                      <ChevronRight className={`w-2.5 h-2.5 absolute right-1 text-gray-400 dark:text-slate-500 transition-transform ${isGroupExpanded ? 'rotate-90' : ''}`} />
                      <span className="fixed ml-[5.5rem] px-2 py-1 bg-gray-900 dark:bg-gray-700 text-white text-[10px] rounded opacity-0 invisible group-hover/cat:opacity-100 group-hover/cat:visible transition-all whitespace-nowrap z-[100] pointer-events-none">
                        {language === 'it' ? category.titleIt : category.titleEn}
                      </span>
                    </button>
                  )
                })()}

                {/* Items */}
                {(!hasTitle || isGroupExpanded) && (
                  <div className={`${collapsed && !sidebarOpen ? 'space-y-3' : 'space-y-1.5'}`}>
                    {category.items
                    .filter((item) => {
                      if ('requiredPlan' in item && item.requiredPlan) {
                        const planOrder = ['trial', 'starter', 'pro', 'business']
                        const userLevel = planOrder.indexOf(user?.plan || 'trial')
                        const reqLevel = planOrder.indexOf(item.requiredPlan)
                        // Piano sufficiente OR modulo add-on attivo
                        if (userLevel >= reqLevel) return true
                        if ('moduleKey' in item && item.moduleKey && hasModule(item.moduleKey)) return true
                        return false
                      }
                      return true
                    })
                    .map((item) => {
                      const isChatItem = item.to === '/chat'
                      const isActiveChatItem = isChatRoute
                      const label = t.nav[item.labelKey]

                      return (
                        <div key={item.to}>
                          <div className="group relative">
                            <NavLink
                              to={getNavTo(item.to)}
                              onClick={() => setSidebarOpen(false)}
                              className={({ isActive }) => `
                                relative flex items-center gap-2 rounded-md
                                transition-colors text-[11px]
                                ${(collapsed && !sidebarOpen) ? 'px-2 py-2.5 justify-center' : 'px-2.5 py-[7px]'}
                                ${isActive || (isChatItem && isActiveChatItem)
                                  ? 'bg-primary-50 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300'
                                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-700/60 hover:text-gray-900 dark:hover:text-white'
                                }
                              `}
                            >
                              {({ isActive }) => (
                                <>
                                  <item.icon className={`${(collapsed && !sidebarOpen) ? 'w-5 h-5' : 'w-4 h-4'} flex-shrink-0 ${
                                    isActive || (isChatItem && isActiveChatItem)
                                      ? 'text-primary-600 dark:text-primary-400'
                                      : 'text-gray-400 dark:text-gray-500'
                                  }`} />
                                  {(!collapsed || sidebarOpen) && (
                                    <>
                                      <span className="truncate flex items-center gap-1.5">
                                        <span>{label}</span>
                                        {'aiEnabled' in item && item.aiEnabled && (
                                          <span className="px-1 py-[1px] rounded text-[7px] font-semibold leading-none uppercase bg-accent-600 text-white">
                                            AI
                                          </span>
                                        )}
                                      </span>
                                      {isChatItem && (
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.preventDefault()
                                            e.stopPropagation()
                                            setChatSubmenuOpen((prev) => !prev)
                                          }}
                                          className="ml-auto p-0.5 rounded hover:bg-gray-200/60 dark:hover:bg-slate-600/60"
                                          aria-label="Toggle chat history"
                                        >
                                          <ChevronDown className={`w-3 h-3 transition-transform ${chatSubmenuOpen ? '' : '-rotate-90'}`} />
                                        </button>
                                      )}
                                    </>
                                  )}
                                  {(collapsed && !sidebarOpen) && (
                                    <span className="fixed ml-[5.5rem] px-2 py-1 bg-gray-900 dark:bg-gray-700 text-white text-[10px] rounded opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all whitespace-nowrap z-[100] pointer-events-none">
                                      {label}
                                    </span>
                                  )}
                                </>
                              )}
                            </NavLink>
                          </div>

                          {isChatItem && chatSubmenuOpen && (!collapsed || sidebarOpen) && (
                            <div className="ml-3.5 mt-px mb-1 border-l border-gray-200 dark:border-slate-700 pl-2 space-y-px">
                              <button
                                type="button"
                                onClick={() => createChatMutation.mutate()}
                                className="w-full text-left text-[10px] px-2 py-0.5 rounded bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/50"
                              >
                                + {t.chat.newChat}
                              </button>
                              {chatHistory.slice(0, 6).map((chat) => {
                                const isActive = location.pathname === '/chat' && new URLSearchParams(location.search).get('c') === chat.id
                                return (
                                  <div key={chat.id} className="flex items-center gap-1">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        navigate(`/chat?c=${chat.id}${location.hash}`)
                                        setSidebarOpen(false)
                                      }}
                                      className={`flex-1 min-w-0 text-left text-[10px] px-2 py-0.5 rounded truncate ${
                                        isActive
                                          ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300'
                                          : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700'
                                      }`}
                                      title={chat.title}
                                    >
                                      {chat.title}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.preventDefault()
                                        e.stopPropagation()
                                        deleteChatMutation.mutate(chat.id)
                                      }}
                                      className="p-1 rounded text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                      aria-label={language === 'it' ? 'Elimina chat' : 'Delete chat'}
                                      title={language === 'it' ? 'Elimina chat' : 'Delete chat'}
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </button>
                                  </div>
                                )
                              })}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
        </nav>

        {/* Workspace Selector - above maturity */}
        {(!collapsed || sidebarOpen) && (
          <div className="flex-shrink-0 border-t border-gray-100 dark:border-slate-700">
            <WorkspaceSelector />
          </div>
        )}

        {/* Maturity Status - fixed at bottom */}
        {currentStateConfig && (
          <div className={`flex-shrink-0 border-t border-gray-100 dark:border-slate-700 ${(collapsed && !sidebarOpen) ? 'p-1.5' : 'px-2.5 py-2'}`}>
            <NavLink
              to="/maturity"
              className={`group relative flex items-center gap-2 w-full py-1 rounded-md
                transition-all hover:bg-gray-50 dark:hover:bg-slate-700/60
                ${(collapsed && !sidebarOpen) ? 'px-2 justify-center' : 'px-2.5'}`}
            >
              <Shield className={`${(collapsed && !sidebarOpen) ? 'w-5 h-5' : 'w-4 h-4'} flex-shrink-0 ${currentStateConfig.color.replace('border-', 'text-')}`} />
              {(!collapsed || sidebarOpen) && (
                <div className="flex-1 min-w-0">
                  <p className={`text-[10px] font-semibold ${currentStateConfig.color.replace('border-', 'text-')}`}>
                    {language === 'it' ? currentStateConfig.nameIt : currentStateConfig.nameEn}
                  </p>
                  <div className="flex gap-px mt-0.5">
                    {Object.keys(MATURITY_STATES).map((state, idx) => (
                      <div
                        key={state}
                        className={`h-0.5 flex-1 rounded-full ${
                          idx <= Object.keys(MATURITY_STATES).indexOf(maturityData?.currentState || 'visibility')
                            ? currentStateConfig.color.replace('border-', 'bg-')
                            : 'bg-gray-200 dark:bg-slate-600'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              )}
              {(collapsed && !sidebarOpen) && (
                <span className="fixed ml-[5.5rem] px-2 py-1 bg-gray-900 dark:bg-gray-700 text-white text-[10px] rounded opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all whitespace-nowrap z-[100] pointer-events-none">
                  {language === 'it' ? currentStateConfig.nameIt : currentStateConfig.nameEn}
                </span>
              )}
            </NavLink>
          </div>
        )}

        {/* Sidebar bottom - only logout fallback when no user */}
        {!user && (
          <div className={`flex-shrink-0 border-t border-gray-100 dark:border-slate-700 p-2`}>
            <button
              onClick={() => onLogout()}
              className={`group relative flex items-center gap-2 w-full py-1 px-2.5 rounded-md
                text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-[11px]
                ${(collapsed && !sidebarOpen) ? 'justify-center' : ''}`}
            >
              <LogOut className={`${(collapsed && !sidebarOpen) ? 'w-5 h-5' : 'w-4 h-4'} flex-shrink-0`} />
              {(!collapsed || sidebarOpen) && (
                <span className="font-medium">{t.nav.logout}</span>
              )}
              {(collapsed && !sidebarOpen) && (
                <span className="fixed ml-[5.5rem] px-2 py-1 bg-gray-900 dark:bg-gray-700 text-white text-[10px] rounded opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all whitespace-nowrap z-[100] pointer-events-none">
                  {t.nav.logout}
                </span>
              )}
            </button>
          </div>
        )}
      </aside>

      {/* User Panel Slide-out from left */}
      {showUserPanel && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 animate-fadeIn"
            onClick={() => setShowUserPanel(false)}
          />
          {/* Panel - slides from right */}
          <div className="fixed top-0 right-0 bottom-0 w-80 max-w-[85vw] z-50 bg-white dark:bg-slate-800 shadow-modal transform transition-transform duration-300 ease-apple animate-slide-right">
            {/* Close button */}
            <button
              onClick={() => setShowUserPanel(false)}
              className="absolute top-4 left-4 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
            >
              <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            </button>

            <div className="h-full flex flex-col px-6 py-6 overflow-y-auto">
              {/* User Info Header */}
              {user && (
                <div className="flex items-center gap-4 mb-6 pb-4 border-b border-gray-200 dark:border-slate-700 pt-8">
                  {user.avatar_url ? (
                    <img 
                      src={user.avatar_url} 
                      alt={user.name || user.email} 
                      className="w-14 h-14 rounded-full object-cover ring-3 ring-primary-100 dark:ring-primary-900/50"
                    />
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-primary-100 dark:bg-primary-900/50 flex items-center justify-center ring-3 ring-primary-50 dark:ring-primary-900/30">
                      <span className="text-xl font-bold text-primary-600 dark:text-primary-400">
                        {(user.name || user.email || '?').charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                      {user.name || 'Utente'}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {user.email}
                    </p>
                  </div>
                </div>
              )}

              {/* License Info */}
              {user?.plan && (
                <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-gray-50 to-gray-100 dark:from-slate-700/50 dark:to-slate-700/30 border border-gray-200 dark:border-slate-600">
                  <div className="flex items-center gap-3 mb-2">
                    {user.plan === 'business' && <Crown className="w-5 h-5 text-purple-500" />}
                    {user.plan === 'pro' && <Sparkles className="w-5 h-5 text-amber-500" />}
                    {user.plan === 'starter' && <Sparkles className="w-5 h-5 text-blue-500" />}
                    {isTrialUser && user.plan === 'trial' && <Sparkles className="w-5 h-5 text-gray-500" />}
                    <div>
                      <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
                        {t.nav.plan || 'Piano'}
                      </p>
                      <p className={`text-sm font-bold uppercase ${
                        user.plan === 'business'
                          ? 'bg-gradient-to-r from-purple-600 to-pink-500 bg-clip-text text-transparent'
                          : user.plan === 'pro'
                          ? 'bg-gradient-to-r from-cyan-500 to-primary-600 bg-clip-text text-transparent'
                          : user.plan === 'starter'
                          ? 'bg-gradient-to-r from-blue-500 to-cyan-500 bg-clip-text text-transparent'
                          : 'text-gray-600 dark:text-gray-300'
                      }`}>
                        {currentPlanDisplay}
                      </p>
                    </div>
                  </div>
                  {isTrialUser && !user.is_trial_expired && (
                    <button
                      onClick={() => {
                        setShowUserPanel(false)
                        setShowUpgradeModal(true)
                      }}
                      className="w-full mt-3 h-[44px] px-4 bg-primary-600 hover:bg-primary-700 hover:-translate-y-px hover:shadow-btn-hover active:scale-[0.98] text-white text-sm font-medium rounded-lg transition-all duration-200 ease-apple"
                    >
                      {t.nav.upgradePlan || 'Passa a Pro'}
                    </button>
                  )}
                </div>
              )}

              {/* Settings */}
              <div className="space-y-2 flex-1">
                {/* Language Toggle */}
                <button
                  onClick={() => setLanguage(language === 'it' ? 'en' : 'it')}
                  className="w-full flex items-center gap-3 p-3 rounded-xl bg-gray-50 dark:bg-slate-700/50 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                >
                  <Globe className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  <div className="text-left flex-1">
                    <p className="text-[11px] text-gray-500 dark:text-gray-400">{t.nav.language || 'Lingua'}</p>
                    <p className="text-xs font-medium text-gray-900 dark:text-white">
                      {language === 'it' ? 'Italiano' : 'English'}
                    </p>
                  </div>
                </button>

                {/* Theme Toggle */}
                <button
                  onClick={toggleTheme}
                  className="w-full flex items-center gap-3 p-3 rounded-xl bg-gray-50 dark:bg-slate-700/50 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                >
                  {isDark ? <Sun className="w-5 h-5 text-amber-500" /> : <Moon className="w-5 h-5 text-indigo-500" />}
                  <div className="text-left flex-1">
                    <p className="text-[11px] text-gray-500 dark:text-gray-400">{t.nav.theme || 'Tema'}</p>
                    <p className="text-xs font-medium text-gray-900 dark:text-white">
                      {isDark ? (t.nav.lightTheme || 'Chiaro') : (t.nav.darkTheme || 'Scuro')}
                    </p>
                  </div>
                </button>
              </div>

              {/* Logout Button - at bottom */}
              <button
                onClick={() => {
                  setShowUserPanel(false)
                  onLogout()
                }}
                className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors font-medium mt-6"
              >
                <LogOut className="w-5 h-5" />
                <span>{t.nav.logout}</span>
              </button>
            </div>
          </div>
        </>
      )}

      {/* ── FISCAL DEADLINES SLIDE-OUT PANEL (Global) ── */}
      {showFiscalPanel && (
        <>
          <div 
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 animate-fadeIn"
            onClick={() => setShowFiscalPanel(false)}
          />
          <div className="fixed top-0 right-0 bottom-0 w-96 max-w-[90vw] z-50 bg-white dark:bg-slate-800 shadow-modal transform transition-transform duration-300 ease-apple animate-slide-right overflow-y-auto">
            <div className="sticky top-0 bg-white dark:bg-slate-800 border-b border-gray-100 dark:border-slate-700 px-5 py-3 flex items-center justify-between z-10">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                <h2 className="text-sm font-semibold text-gray-900 dark:text-white">{t.dashboard.fiscalDeadlines}</h2>
                {urgentDeadlines > 0 && (
                  <span className="flex items-center gap-1 text-[10px] bg-red-100 dark:bg-red-900/50 text-red-600 dark:text-red-400 px-1.5 py-0.5 rounded-full">
                    <AlertTriangle className="w-2.5 h-2.5" /> {urgentDeadlines} {t.dashboard.urgent}
                  </span>
                )}
              </div>
              <button onClick={() => setShowFiscalPanel(false)} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors">
                <X className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              </button>
            </div>
            <div className="px-5 py-3 space-y-2">
              {fiscalLoading ? (
                <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-blue-500" /></div>
              ) : fiscalData && fiscalData.deadlines.length > 0 ? (
                fiscalData.deadlines.map((deadline) => {
                  const deadlineDate = new Date(deadline.date)
                  const today = new Date(); today.setHours(0,0,0,0)
                  const diffDays = Math.ceil((deadlineDate.getTime() - today.getTime()) / (1000*60*60*24))
                  const isUrgent = diffDays <= 7
                  const isUpcoming = diffDays > 7 && diffDays <= 30
                  return (
                    <div key={deadline.id} className={`flex items-center gap-3 p-2.5 rounded-lg transition-colors ${
                      isUrgent ? 'bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800/50' 
                        : isUpcoming ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800/50'
                        : 'bg-gray-50 dark:bg-slate-800 border border-gray-100 dark:border-slate-700'
                    }`}>
                      <div className="flex-shrink-0 text-center w-9">
                        <span className="text-base">{deadline.icon}</span>
                        <p className={`text-[10px] font-bold mt-0.5 ${isUrgent ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>
                          {deadlineDate.toLocaleDateString(language === 'it' ? 'it-IT' : 'en-US', { day: '2-digit', month: 'short' })}
                        </p>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 dark:text-white text-xs truncate">{deadline.title}</p>
                        <p className="text-[10px] text-gray-500 dark:text-gray-400 truncate">{deadline.description}</p>
                        <span className={`inline-block mt-0.5 text-[9px] px-1.5 py-0.5 rounded-full font-medium ${categoryColors[deadline.category]}`}>
                          {deadline.category.toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-shrink-0 text-right">
                        {diffDays === 0 ? (
                          <span className="flex items-center gap-1 text-[10px] font-bold text-red-600 dark:text-red-400"><AlertTriangle className="w-2.5 h-2.5" />{t.dashboard.today}</span>
                        ) : diffDays === 1 ? (
                          <span className="flex items-center gap-1 text-[10px] font-bold text-red-600 dark:text-red-400"><Clock className="w-2.5 h-2.5" />{t.dashboard.tomorrow}</span>
                        ) : (
                          <span className={`flex items-center gap-1 text-[10px] font-medium ${isUrgent ? 'text-red-600 dark:text-red-400' : isUpcoming ? 'text-amber-600 dark:text-amber-400' : 'text-gray-500 dark:text-gray-400'}`}>
                            <Clock className="w-2.5 h-2.5" />{diffDays}{t.dashboard.days}
                          </span>
                        )}
                      </div>
                    </div>
                  )
                })
              ) : (
                <p className="text-gray-500 dark:text-gray-400 text-center py-8 text-xs">{t.dashboard.noDeadlines}</p>
              )}
            </div>
            <div className="sticky bottom-0 bg-white dark:bg-slate-800 border-t border-gray-100 dark:border-slate-700 px-5 py-2">
              <p className="text-[9px] text-gray-400 dark:text-gray-500 flex items-center gap-1">
                <Calendar className="w-2.5 h-2.5" /> {t.dashboard.fiscalDisclaimer}
              </p>
            </div>
          </div>
        </>
      )}

      {/* Main content area */}
      <div className={`flex-1 flex flex-col transition-all duration-200 ${collapsed ? 'lg:pl-[4.5rem]' : 'lg:pl-64'}`}>
        {/* Desktop Top Bar — Agicap-style with horizontal section tabs */}
        <header className="hidden lg:flex sticky top-0 z-30 bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 h-14 items-center px-5">
          {/* Left: Section tabs */}
          <nav className="flex items-center gap-1 flex-1 min-w-0">
            {topSections.map((section) => {
              const isActive = section.path
                ? section.path === '/' ? location.pathname === '/' : location.pathname.startsWith(section.path)
                : section.children?.some(c => location.pathname.startsWith(c))
              const label = language === 'it' ? section.labelIt : section.labelEn

              if (section.path) {
                // Special handling for Dashboards tab — add dropdown arrow
                if (section.id === 'dashboard') {
                  return (
                    <div key={section.id} className="flex items-center">
                      <NavLink
                        to={`/${location.hash}`}
                        className={`relative flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium rounded-l-md transition-colors whitespace-nowrap ${
                          isActive
                            ? 'text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/40'
                            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-slate-700/60'
                        }`}
                      >
                        {label}
                        {isActive && <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-primary-600 dark:bg-primary-400 rounded-full" />}
                      </NavLink>
                      <button
                        onClick={() => setShowDashboardSelector(true)}
                        className={`px-1 py-2.5 text-sm rounded-r-md transition-colors ${
                          isActive
                            ? 'text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/40 hover:bg-primary-100 dark:hover:bg-primary-900/60'
                            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-slate-700/60'
                        }`}
                        title={language === 'it' ? 'Gestisci dashboard' : 'Manage dashboards'}
                      >
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )
                }
                return (
                  <NavLink
                    key={section.id}
                    to={`${section.path}${location.hash}`}
                    className={`relative flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                      isActive
                        ? 'text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/40'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-slate-700/60'
                    }`}
                  >
                    {label}
                    {section.badge && pendingCount > 0 && (
                      <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-bold text-white bg-orange-500 rounded-md leading-none">
                        {pendingCount}
                      </span>
                    )}
                    {section.diamond && (
                      <span className="inline-flex items-center justify-center w-4 h-4 text-[8px] text-purple-600 dark:text-purple-400">⬡</span>
                    )}
                    {isActive && <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-primary-600 dark:bg-primary-400 rounded-full" />}
                  </NavLink>
                )
              }
              
              // Dropdown section (Contabilità, Area Finanziaria)  
              return (
                <div key={section.id} className="relative group/section">
                  <button
                    className={`flex items-center gap-1 px-3 py-2.5 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                      isActive
                        ? 'text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/40'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-slate-700/60'
                    }`}
                  >
                    {label}
                    <ChevronDown className="w-3.5 h-3.5" />
                    {isActive && <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-primary-600 dark:bg-primary-400 rounded-full" />}
                  </button>
                  {/* Dropdown */}
                  <div className="absolute top-full left-0 mt-0.5 w-64 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-gray-200 dark:border-slate-700 py-1 opacity-0 invisible group-hover/section:opacity-100 group-hover/section:visible transition-all z-50">
                    {section.children?.map(childPath => {
                      // First check topNavItemLookup, then fall back to navCategories
                      const topItem = topNavItemLookup[childPath]
                      let childLabel: string
                      let desc: string
                      let NavIcon: ComponentType<{ className?: string }>
                      if (topItem) {
                        childLabel = language === 'it' ? topItem.labelIt : topItem.labelEn
                        desc = (language === 'it' ? topItem.descIt : topItem.descEn) || ''
                        NavIcon = topItem.icon
                      } else {
                        const allItems: Array<{ to: string; icon: ComponentType<{ className?: string }>; labelKey: string }> = []
                        navCategories.forEach(c => c.items.forEach(i => allItems.push(i)))
                        const navItem = allItems.find(i => i.to === childPath)
                        if (!navItem) return null
                        childLabel = (t.nav as Record<string, string>)[navItem.labelKey] || navItem.labelKey
                        desc = (t.nav as Record<string, string>)[`${navItem.labelKey}Desc`] || ''
                        NavIcon = navItem.icon
                      }
                      const isChildActive = location.pathname === childPath
                      return (
                        <NavLink
                          key={childPath}
                          to={`${childPath}${location.hash}`}
                          className={`flex items-center gap-3 px-3 py-2 mx-1 rounded-md transition-colors ${
                            isChildActive
                              ? 'bg-primary-50 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300'
                              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700/60'
                          }`}
                        >
                          <NavIcon className={`w-4 h-4 flex-shrink-0 ${isChildActive ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 dark:text-gray-500'}`} />
                          <div className="min-w-0">
                            <p className="text-xs font-medium truncate">{childLabel}</p>
                            {desc && <p className="text-[10px] text-gray-400 dark:text-gray-500 truncate">{desc}</p>}
                          </div>
                        </NavLink>
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </nav>

          {/* Right: Search + Theme + Bell + User dropdown */}
          <div className="flex items-center gap-1">
            {/* Theme toggle */}
            <button
              onClick={toggleTheme}
              className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
              title={isDark ? t.nav.lightTheme : t.nav.darkTheme}
            >
              {isDark ? <Sun className="w-4 h-4 text-amber-500" /> : <Moon className="w-4 h-4 text-indigo-500" />}
            </button>

            {/* Upload shortcut */}
            <button
              onClick={() => navigate('/upload')}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white text-xs font-medium shadow-sm transition-colors"
              title={language === 'it' ? 'Carica dati' : 'Upload data'}
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">{language === 'it' ? 'Carica' : 'Upload'}</span>
            </button>

            {/* Search */}
            <button
              onClick={() => navigate('/chat')}
              className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
              title={t.common.search}
            >
              <Search className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            </button>

            {/* Fiscal Deadlines Bell */}
            <button
              onClick={() => setShowFiscalPanel(true)}
              className="relative p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
              title={t.dashboard.fiscalDeadlines}
            >
              <Bell className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              {upcomingDeadlines > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[14px] h-[14px] flex items-center justify-center text-[8px] font-bold text-white bg-red-500 rounded-full leading-none">
                  {upcomingDeadlines > 9 ? '9+' : upcomingDeadlines}
                </span>
              )}
            </button>

            {/* User dropdown — Agicap-style with Import/Export/Settings */}
            {user ? (
              <div className="relative group/user">
                <button
                  className="flex items-center gap-2 py-1.5 px-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                >
                  {user.avatar_url ? (
                    <img 
                      src={user.avatar_url} 
                      alt={user.name || user.email} 
                      className="w-6 h-6 rounded-full object-cover ring-1 ring-gray-200 dark:ring-slate-600"
                    />
                  ) : (
                    <div className="w-6 h-6 rounded-full bg-primary-100 dark:bg-primary-900/50 flex items-center justify-center ring-1 ring-gray-200 dark:ring-slate-600">
                      <span className="text-[10px] font-semibold text-primary-600 dark:text-primary-400">
                        {(user.name || user.email || '?').charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                  <span className="text-xs text-gray-600 dark:text-gray-300 max-w-[120px] truncate">
                    {user.name || user.email}
                  </span>
                  <ChevronDown className="w-3 h-3 text-gray-400" />
                </button>

                {/* User dropdown menu */}
                <div className="absolute top-full right-0 mt-0.5 w-52 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-gray-200 dark:border-slate-700 py-1 opacity-0 invisible group-hover/user:opacity-100 group-hover/user:visible transition-all z-50">
                  <NavLink
                    to={`/upload${location.hash}`}
                    className="flex items-center gap-3 px-3 py-2.5 mx-1 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700/60 transition-colors"
                  >
                    <Download className="w-4 h-4 text-gray-400" />
                    <span className="text-xs font-medium">{t.nav.importData}</span>
                  </NavLink>
                  <button
                    onClick={() => {
                      // trigger export
                      window.print()
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 mx-1 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700/60 transition-colors"
                  >
                    <Upload className="w-4 h-4 text-gray-400" />
                    <span className="text-xs font-medium">{t.nav.exportData}</span>
                  </button>
                  <div className="border-t border-gray-100 dark:border-slate-700 my-1" />
                  <NavLink
                    to={`/integrations${location.hash}`}
                    className="flex items-center gap-3 px-3 py-2.5 mx-1 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700/60 transition-colors"
                  >
                    <Link2 className="w-4 h-4 text-gray-400" />
                    <span className="text-xs font-medium">{t.nav.integrations}</span>
                  </NavLink>
                  <NavLink
                    to={`/account${location.hash}`}
                    className="flex items-center gap-3 px-3 py-2.5 mx-1 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700/60 transition-colors"
                  >
                    <Settings className="w-4 h-4 text-gray-400" />
                    <span className="text-xs font-medium">{t.nav.settings}</span>
                  </NavLink>
                  <button
                    onClick={() => setShowUserPanel(true)}
                    className="w-full flex items-center gap-3 px-3 py-2.5 mx-1 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700/60 transition-colors"
                  >
                    <UserIcon className="w-4 h-4 text-gray-400" />
                    <span className="text-xs font-medium">{user.name || 'Profilo'}</span>
                    {user.plan && (
                      <span className={`ml-auto text-[8px] font-bold uppercase ${
                        user.plan === 'business' ? 'text-purple-500' : user.plan === 'pro' ? 'text-amber-500' : user.plan === 'starter' ? 'text-blue-500' : 'text-gray-400'
                      }`}>
                        {user.plan}
                      </span>
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => onLogout()}
                className="flex items-center gap-2 py-1.5 px-3 rounded-lg text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-sm"
              >
                <LogOut className="w-4 h-4" />
                <span>{t.nav.logout}</span>
              </button>
            )}
          </div>
        </header>

        {/* Quick opener when sidebar is collapsed on chat page */}
        {collapsed && isChatRoute && (
          <button
            type="button"
            onClick={() => {
              setCollapsed(false)
              setChatSubmenuOpen(true)
            }}
            className="hidden lg:flex fixed left-[5.5rem] top-16 z-20 items-center gap-2 px-3 py-1.5 rounded-lg bg-white/95 dark:bg-slate-800/95 border border-gray-200 dark:border-slate-600 shadow-sm text-xs text-gray-700 dark:text-gray-200"
          >
            <MessageCircle className="w-3.5 h-3.5" />
            <span>Storico chat</span>
            <ChevronDown className="w-3.5 h-3.5" />
          </button>
        )}

        {/* Mobile header */}
        <header className="sticky top-0 z-30 bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 lg:hidden">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSidebarOpen(true)}
                className="p-2 -ml-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700"
              >
                <Menu className="w-5 h-5 text-gray-600 dark:text-gray-300" />
              </button>
              {currentPage && (
                <div className="flex items-center gap-1.5">
                  <currentPage.icon className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">{currentPage.label}</span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setShowFiscalPanel(true)}
                className="relative p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700"
              >
                <Bell className="w-4.5 h-4.5 text-gray-500 dark:text-gray-400" />
                {upcomingDeadlines > 0 && (
                  <span className="absolute top-1 right-1 min-w-[14px] h-[14px] flex items-center justify-center px-0.5 text-[8px] font-bold text-white bg-red-500 rounded-full leading-none">
                    {upcomingDeadlines > 9 ? '9+' : upcomingDeadlines}
                  </span>
                )}
              </button>
              <button
                onClick={() => setShowUserPanel(true)}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700"
              >
                {user?.avatar_url ? (
                  <img src={user.avatar_url} alt="" className="w-6 h-6 rounded-full object-cover" />
                ) : (
                  <UserIcon className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                )}
              </button>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-6 !pt-5">
          {(queueProcessing > 0 || queueQueued > 0) && (
            <div className="mb-3 rounded-xl border border-blue-200 dark:border-blue-800/60 bg-blue-50/80 dark:bg-blue-950/30 px-4 py-2.5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-xs text-blue-800 dark:text-blue-200">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  <span className="font-medium">
                    {language === 'it'
                      ? `${queueProcessing} in elaborazione, ${queueQueued} in coda`
                      : `${queueProcessing} processing, ${queueQueued} queued`}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => navigate(getNavTo('/upload'))}
                  className="text-[11px] font-medium text-blue-700 dark:text-blue-300 hover:text-blue-900 dark:hover:text-blue-100"
                >
                  {language === 'it' ? 'Apri coda upload' : 'Open upload queue'}
                </button>
              </div>
              {activeQueueEntries.length > 0 && (
                <p className="mt-1.5 text-[11px] text-blue-700 dark:text-blue-300 truncate">
                  {activeQueueEntries.map((item) => item.filename).join(' \u2022 ')}
                </p>
              )}
            </div>
          )}
          {children}
        </main>

        {/* Footer */}
        <footer className="flex-shrink-0 py-2 px-4 lg:px-6 border-t border-gray-100 dark:border-slate-700">
          <div className="flex items-center justify-center gap-2 text-[9px] text-gray-400 dark:text-gray-500">
            <span className="font-semibold tracking-wider uppercase">BETA</span>
            <span>|</span>
            <span><span className="text-gray-900 dark:text-white font-bold font-display">PongoFin</span> <span className="font-display">Financial Intelligence</span></span>
          </div>
        </footer>
      </div>

      {/* Upgrade Modal - shows when trial expires */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        trialExpired={user?.is_trial_expired}
        trialDaysLeft={user?.trial_days_left}
                        currentPlan={user?.plan}
      />

      {/* Dashboard Selector Modal */}
      {showDashboardSelector && (
        <DashboardSelector onClose={() => setShowDashboardSelector(false)} />
      )}
    </div>
  )
}
